import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClients } from '../store/clientsSlice';
import './TaxComputation.css';
import { INCOME_TAX_HEAD_OPTIONS, getOptionLabel } from '../constants/taxOptions';
import { buildAuthHeaders, getAccessToken } from '../utils/authHeaders';

const apiUrl = process.env.REACT_APP_API_URL;

const formatAmount = (value) => {
  const number = Number(value ?? 0);
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

const summaryRows = (data) => [
  { label: 'F&O Net Income', amount: data.fnoNet },
  { label: 'Intraday Net Income', amount: data.intradayNet },
  { label: 'Delivery Business Net Income', amount: data.deliveryBusinessNet },
  { label: 'Business Income Before Tax Depreciation', amount: data.businessIncomeBeforeTaxDepreciation },
  { label: 'Less: Tax Depreciation', amount: data.taxDepreciation },
  { label: 'Business Income After Tax Depreciation', amount: data.businessIncomeAfterTaxDepreciation },
  { label: 'Dividend Income', amount: data.dividendIncome },
  { label: 'Interest Income', amount: data.interestIncome },
  { label: 'Other Sources Income', amount: data.otherSourcesIncome },
  { label: 'Total Manual Income', amount: data.otherIncome },
  { label: 'Short-Term Capital Gain', amount: data.shortTermCapitalGain },
  { label: 'Long-Term Capital Gain Before 112A Relief', amount: data.longTermCapitalGainGross },
  { label: 'Less: 112A Threshold Relief', amount: data.longTermCapitalGainThresholdRelief },
  { label: 'Taxable Long-Term Capital Gain', amount: data.longTermCapitalGain },
  { label: 'Gross Total Income', amount: data.grossTotalIncome, highlight: true },
];

const TaxComputation = () => {
  const dispatch = useDispatch();
  const { clientId, financialYear } = useSelector((state) => state.app);
  const accessToken = useSelector((state) => state.auth.access_token);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchClients());
  }, [dispatch]);

  useEffect(() => {
    if (!clientId || !financialYear) {
      return;
    }

    setLoading(true);
    axios.get(`${apiUrl}/api/tax-computation/report`, {
      params: {
        clientId,
        financialYear,
      },
      headers: buildAuthHeaders(getAccessToken(accessToken)),
    })
      .then((response) => {
        setData(response.data);
        setError('');
      })
      .catch((err) => {
        console.error('Error fetching tax computation report', err);
        setError('Error fetching tax computation report.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [accessToken, clientId, financialYear]);

  return (
    <div className="tax-computation-page">
      <div className="tax-computation-hero">
        <div>
          <h2>Tax Computation and Depreciation Schedule</h2>
          <p>Tax-purpose working report for business income, capital gains, and depreciation schedule.</p>
        </div>
        <div className="tax-computation-badge">FY {financialYear}</div>
      </div>

      {loading && <p className="tax-computation-message">Loading tax report...</p>}
      {error && <p className="tax-computation-error">{error}</p>}

      {data && (
        <>
          <section className="tax-card-grid">
            <article className="tax-card">
              <span className="tax-card-label">Business Income After Tax Depreciation</span>
              <strong>₹ {formatAmount(data.businessIncomeAfterTaxDepreciation)}</strong>
            </article>
            <article className="tax-card">
              <span className="tax-card-label">Tax Depreciation</span>
              <strong>₹ {formatAmount(data.taxDepreciation)}</strong>
            </article>
            <article className="tax-card tax-card-accent">
              <span className="tax-card-label">Gross Total Income</span>
              <strong>₹ {formatAmount(data.grossTotalIncome)}</strong>
            </article>
          </section>

          <section className="tax-section">
            <h3>Tax Computation Summary</h3>
            <div className="tax-table-wrap">
              <table className="tax-table">
                <thead>
                  <tr>
                    <th>Particulars</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryRows(data).map((row) => (
                    <tr key={row.label} className={row.highlight ? 'tax-highlight-row' : ''}>
                      <td>{row.label}</td>
                      <td>₹ {formatAmount(row.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="tax-section">
            <h3>Manual Income Detail</h3>
            {data.manualIncomeEntries && data.manualIncomeEntries.length > 0 ? (
              <div className="tax-table-wrap">
                <table className="tax-table">
                  <thead>
                    <tr>
                      <th>Tax Head</th>
                      <th>Income Head</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.manualIncomeEntries.map((item) => (
                      <tr key={`${item.taxHead}-${item.label}`}>
                        <td>{getOptionLabel(INCOME_TAX_HEAD_OPTIONS, item.taxHead, item.taxHead)}</td>
                        <td>{item.label}</td>
                        <td>₹ {formatAmount(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="tax-computation-message">No other income entries available for this financial year.</p>
            )}
          </section>

          <section className="tax-section">
            <h3>Depreciation Schedule For Tax Purpose</h3>
            {data.depreciationSchedule?.blocks && data.depreciationSchedule.blocks.length > 0 ? (
              <div className="tax-table-wrap">
                <table className="tax-table tax-schedule-table">
                  <thead>
                    <tr>
                      <th>Tax Block</th>
                      <th>Rate %</th>
                      <th>Opening WDV</th>
                      <th>Full-Rate Additions</th>
                      <th>Half-Rate Additions</th>
                      <th>Depreciation</th>
                      <th>Closing WDV</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.depreciationSchedule.blocks.map((block) => (
                      <tr key={`${block.taxBlock}-${block.depreciationRate}`}>
                        <td>{block.taxBlock}</td>
                        <td>{formatAmount(block.depreciationRate)}</td>
                        <td>₹ {formatAmount(block.openingWdv)}</td>
                        <td>₹ {formatAmount(block.additionsEligibleForFullRate)}</td>
                        <td>₹ {formatAmount(block.additionsEligibleForHalfRate)}</td>
                        <td>₹ {formatAmount(block.depreciationForYear)}</td>
                        <td>₹ {formatAmount(block.closingWdv)}</td>
                      </tr>
                    ))}
                    <tr className="tax-highlight-row">
                      <td colSpan="5">Total</td>
                      <td>₹ {formatAmount(data.depreciationSchedule.totalTaxDepreciation)}</td>
                      <td>₹ {formatAmount(data.depreciationSchedule.totalClosingWdv)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="tax-computation-message">No tax depreciation blocks available for this financial year.</p>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default TaxComputation;